import {useCallback, useRef, useState} from 'react';

interface PanState {
  isPanning: boolean;
  cursor: 'grab' | 'grabbing';
}

export function usePan(scrollContainerRef: React.RefObject<HTMLDivElement | null>) {
  const [panState, setPanState] = useState<PanState>({isPanning: false, cursor: 'grab'});
  const startPos = useRef<{x: number; y: number; scrollLeft: number; scrollTop: number} | null>(
    null
  );

  const onMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const container = scrollContainerRef.current;
      if (!container) {
        return;
      }
      // Only pan with primary button, not on interactive elements
      if (e.button !== 0) {
        return;
      }
      // Don't initiate panning when the click originates on an interactive SVG part.
      // Calling e.preventDefault() on mousedown suppresses the click event on SVG elements
      // in several browser/React combinations, so we bail out entirely here.
      if (e.target instanceof Element && e.target.closest('[role="button"]')) {
        return;
      }
      startPos.current = {
        x: e.clientX,
        y: e.clientY,
        scrollLeft: container.scrollLeft,
        scrollTop: container.scrollTop,
      };
      setPanState({isPanning: true, cursor: 'grabbing'});
      e.preventDefault();
    },
    [scrollContainerRef]
  );

  const onMouseMove = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!startPos.current) {
        return;
      }
      const container = scrollContainerRef.current;
      if (!container) {
        return;
      }
      const dx = e.clientX - startPos.current.x;
      const dy = e.clientY - startPos.current.y;
      container.scrollLeft = startPos.current.scrollLeft - dx;
      container.scrollTop = startPos.current.scrollTop - dy;
    },
    [scrollContainerRef]
  );

  const onMouseUp = useCallback(() => {
    startPos.current = null;
    setPanState({isPanning: false, cursor: 'grab'});
  }, []);

  const onMouseLeave = useCallback(() => {
    startPos.current = null;
    setPanState({isPanning: false, cursor: 'grab'});
  }, []);

  return {
    isPanning: panState.isPanning,
    cursor: panState.cursor,
    onMouseDown,
    onMouseMove,
    onMouseUp,
    onMouseLeave,
  };
}
